<template>
  <div>
    <b-card>
      <h2 v-if="!readonly" v-translate>Search and Edit</h2>
      <b-row>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-from" v-translate>Area From</label>
            <b-form-select id="quote-area-from" v-model="filter.quoteAreaFrom" :options="areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-to" v-translate>Area To</label>
            <b-form-select id="quote-area-to" v-model="filter.quoteAreaTo" :options="areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-name" v-translate>Postal Code</label>
            <input id="quote-name" v-model.lazy="filter.quotePostalCode" type="text" class="mb-2 col-12 form-control">
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-shipping-type" v-translate>Shipping Type</label>
            <b-form-select id="quote-shipping-type" v-model="filter.quoteShippingType" :options="this.shippingTypes.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Size</label>
            <b-form-select id="quote-size" v-model="filter.quoteShippingSizeType" :options="this.sizeTypes.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4" v-if="!readonly">
          <div class="form-group">
            <label for="quote-size" v-translate>Imported Quotes CSV</label>
            <b-form-file
              v-model="fileImported"
              :placeholder="$gettext('Choose an Imported CSV or drop it here...')"
              :drop-placeholder="$gettext('Drop file here...')"
              @input="handleFileImportedSelect"
              ref="importedQuotes"
              id="importedQuotes"
              accept=".csv"
            ></b-form-file>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Client</label>
            <company-autocomplete ref="companyAc" @selected="onSearchCompanySelected" v-if="filter.quoteCompanies.length === 0" />
            <div v-for="(item, index) in filter.quoteCompanies" :key="index" v-else>
              {{ item.name }} <b-badge variant="danger" href="#" @click.prevent="removeCompanyFromFilters(index)">×</b-badge>
            </div>&nbsp;
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Provider</label>
            <company-autocomplete ref="providerAc" @selected="onSearchProviderSelected" v-if="filter.quoteProviders.length === 0" />
            <div v-for="(item, index) in filter.quoteProviders" :key="index" v-else>
              {{ item.name }} <b-badge variant="danger" href="#" @click.prevent="removeProviderFromFilters(index)">×</b-badge>
            </div>&nbsp;
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <label for="quote-size">&nbsp;</label>
          <b-form-row>
            <b-col>
              <b-button size="sm" block variant="success" @click="searchQuotes" class="mb-2" :disabled="working">
                <b-spinner small v-if="working" />
                <translate>Search</translate>
              </b-button>
            </b-col>
            <b-col>
              <b-button size="sm" block variant="danger" @click="resetQuoteSearch" class="mb-2" :disabled="working">
                <translate>Reset Search</translate>
              </b-button>
            </b-col>
          </b-form-row>
        </b-col>
      </b-row>
      <div v-if="searchedQuotes.length && !readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="primary" @click="backupSelected" :disabled="selected.length === 0 || deleting" v-translate>
          Backup Selected
        </b-button>
        <b-button variant="warning" v-b-modal.modalEditPrices :disabled="selected.length === 0 || !backuped || deleting" v-translate>
          EDIT Selected
        </b-button>
        <b-button variant="danger" @click="deleteSelected" :disabled="selected.length === 0 || !backuped || deleting" v-translate>
          DEACTIVATE Selected
        </b-button>
      </div>
      <div v-if="searchedQuotes.length && readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="warning" v-b-modal.modalEditPrices v-translate>
          VIEW Selected
        </b-button>
      </div>
      <b-modal id="modalEditPrices" size="xl" ref="modalEditPrices" hide-footer @hidden="resetEditPrices">
        <div slot="modal-title" v-translate>Bulk Edit Prices</div>
        <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
        <b-row v-if="updating">
          <b-col>
            <b-progress :max="totalToUpdate.length" height="2rem" class="mb-3">
              <b-progress-bar :value="updatedCount">
                <span v-translate="{count: updatedCount, total: totalToUpdate.length}">Progress: <strong>%{count} / %{total}</strong></span>
              </b-progress-bar>
            </b-progress>
          </b-col>
        </b-row>
        <b-row v-if="!readonly">
          <b-col>
            <h4 v-translate>Activate/Deactivate</h4>
            <b-row>
              <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="search-for" v-translate>Set All Selected As</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-select id="search-for" v-model="active" :options="[{text: $gettext('Active'), value: 1}, {text: $gettext('Not Active'), value: 0}]"></b-form-select>
                  </b-col>
                </b-row>
              </b-col>
              <!-- <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="replace-with" v-translate>Replace with:</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-input id="replace-with" v-model="replaceWith"></b-form-input>
                  </b-col>
                </b-row>
              </b-col> -->
              <b-col sm="4">
                <b-button @click="bulkReplaceActive" :disabled="updating" v-translate>Bulk Set Active Status</b-button>
              </b-col>
            </b-row>
            <hr>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="3">
            <h4 v-translate>Prices</h4>
            <b-row class="mb-3" v-if="!readonly">
              <b-col sm="5">
                <label for="price-by-percentage" v-translate>Change All By Percentage</label>
              </b-col>
              <b-col sm="7">
                <b-input-group append="%">
                  <b-form-input id="price-by-percentage" v-model="priceByPercentage" :disabled="pricesIsNan"></b-form-input>
                </b-input-group>
                <b-form-checkbox :disabled="pricesIsNan" v-model="pricesAsIntegers"><translate>Force Integers</translate></b-form-checkbox>
              </b-col>
            </b-row>
            <b-row class="mb-1" v-for="(price, i) in distinctPrices" :key="'price' + i">
              <b-col sm="5">
                <label :for="`price-${i}`">{{ price }}</label>
              </b-col>
              <b-col sm="7" v-if="!readonly">
                <b-form-input :id="`price-${i}`" v-model="newPrices[price]"></b-form-input>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-button @click="bulkUpdatePrices" :disabled="updating" v-if="!readonly" v-translate>Update</b-button>
      </b-modal>
      <b-alert variant="danger" :show="deleting">
        <span v-translate="{count: count, total: selected.length}">Deleting %{count}/%{total}</span>
      </b-alert>
      <p v-if="total" v-translate="{total: this.total}">Total: <strong>%{total}</strong></p>
      <b-table id="quoteProvidersTable" ref="quoteProvidersTable" @row-selected="rowSelected" :busy.sync="working" selectable stacked="md" foot-clone :items="searchedQuotes" :fields="quotesFields" v-if="searchedQuotes.length > 0">
        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong v-translate>Loading...</strong>
        </div>
        <template v-slot:cell(reference)="data">
          <a :href="'/edit/outsourcing-quotes/' + data.item.id" target="_blank" v-if="!readonly">{{data.item.reference}}</a>
          <span v-else>{{data.item.reference}}</span>
        </template>
        <template v-slot:cell(size_id)="data">
          {{ data.item.size_id ? sizeTypes.findIndex(x => x.id === data.item.size_id) !== -1 ? sizeTypes.find(x => x.id === data.item.size_id).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_from)="data">
          {{ data.item.area_id_from ? areas.findIndex(x => x.id === data.item.area_id_from) !== -1 ? areas.find(x => x.id === data.item.area_id_from).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_to)="data">
          {{ data.item.area_id_to ? areas.findIndex(x => x.id === data.item.area_id_to) !== -1 ? areas.find(x => x.id === data.item.area_id_to).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(platform_id)="data">
          {{ data.item.platform_id ? platforms.find(x => x.id === data.item.platform_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(shipping_type_id)="data">
          {{ data.item.shipping_type_id ? shippingTypes.find(x => x.id === data.item.shipping_type_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(company_id_customer)="row">
          <strong class="badge" v-if="row.item.company" :style="`background-color: ${stringToColour(' ' + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id)};${textContrastColor(stringToColour(' ' + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id))}`">{{row.item.company.name || row.item.company.id}}</strong>
        </template>
        <template v-slot:cell(company_id_provider)="row">
          <strong class="badge" :style="`background-color: ${stringToColour(' ' + row.item.provider.id + row.item.provider.id + row.item.provider.id + row.item.provider.id + row.item.provider.id)};${textContrastColor(stringToColour(' ' + row.item.provider.id + row.item.provider.id + row.item.provider.id + row.item.provider.id + row.item.provider.id))}`">{{row.item.provider.name || row.item.provider.id}}</strong>
        </template>
      </b-table>
    </b-card>
    <b-card v-if="!readonly">
      <h2 v-translate>Import</h2>
      <b-form-file
        v-model="file"
        :placeholder="$gettext('Choose a file or drop it here...')"
        :drop-placeholder="$gettext('Drop file here...')"
        @input="handleFileSelect"
        ref="csvToImport"
        id="csvToImport"
        accept=".csv"
      ></b-form-file>
      <b-alert variant="success" :show="importing" class="mt-3">
        <span v-translate="{count: count, total: importData.length}">Importing %{count}/%{total}</span>
      </b-alert>
      <b-alert variant="info" :show="importData.length > 0 && errors.length === 0" class="mt-3">
        <h5 v-translate="{total: importData.length}">It seems there are no errors and you are about to bulk import %{total} quotes. Is it OK?</h5>
        <b-button variant="success" @click="bulkImport" :disabled="importing">
          <b-spinner small v-show="importing" />
          <translate>OK! Let's import them!</translate>
        </b-button>
        <b-button variant="danger" @click="file = null; importData = []" :disabled="importing" v-translate>
          NOPE! Cancel it!
        </b-button>
      </b-alert>
      <b-alert variant="danger" :show="errors.length > 0" class="mt-3">
        <h5 v-translate>It seems there are errors, check 'em out:</h5>
        <ul>
          <li v-for="(item, index) in errors" :key="index">
            {{ item.message }}{{item.row ? ' (row: ' + item.row + (item.index ? ' - col: ' + item.index : '') + ')' : ''}}
          </li>
        </ul>
        <p v-translate>Please, review and fix your CSV file and upload it again</p>
      </b-alert>
    </b-card>
  </div>
</template>
<script>
  import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
  import Papa from 'papaparse'

  export default {
    name: 'BulkQuote3PList',
    components: {
      CompanyAutocomplete
    },
    props: {
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        file: null,
        fileImported: null,
        importData: [],
        errors: [],
        quotesFields: [
          {key: 'company_id_customer', label: this.$gettext('Client')},
          {key: 'company_id_provider', label: this.$gettext('Provider')},
          {key: 'area_id_from', label: this.$gettext('From')},
          {key: 'area_id_to', label: this.$gettext('To')},
          {key: 'postal_code_to', label: this.$gettext('Postal Code')},
          {key: 'shipping_type_id', label: this.$gettext('Type')},
          {key: 'size_id', label: this.$gettext('Size')},
          {key: 'price', label: this.$gettext('Price')},
          {key: 'applies_automatically', label: this.$gettext('Applies Automatically')},
          {key: 'distribution_factor', label: this.$gettext('Distribution Factor')},
          {key: 'active', label: this.$gettext('Active')}
        ],
        filters: [],
        filter: {
          ids: [],
          quoteName: '',
          quoteIncludes: null,
          quoteAreaFrom: null,
          quoteAreaTo: null,
          quoteCompanies: [],
          quoteProviders: [],
          quoteShippingType: null,
          quoteShippingSizeType: null
        },
        working: false,
        searchedQuotes: [],
        companiesQuotes: [],
        companyAcOptions: {
          debounce: 500,
          placeholder: this.$gettext('Company Name, Business Name or TIN'),
          inputClass: 'form-control'
        },
        searchCompanyQuery: '',
        minCharacter: 3,
        areas: [],
        platforms: [],
        platformsSelect: [],
        shippingTypes: [],
        shippingTypesSelect: [],
        sizeTypes: [],
        sizeTypesSelect: [],
        selected: [],
        selectAllTrigger: false,
        backuped: false,
        importing: false,
        deleting: false,
        count: 0,
        batchUpdateChunkLength: 15,
        total: null,
        updating: false,
        totalToUpdate: 0,
        updatedCount: 0,
        priceByPercentage: null,
        newPrices: {},
        pricesAsIntegers: false,
        searchFor: null,
        replaceWith: null,
        active: 1
      }
    },
    computed: {
      distinctPrices() {
        return [...new Set(this.selected.map(row => row.price))]
      },
      pricesIsNan() {
        return !!this.distinctPrices.filter(x => isNaN(x))[0]
      }
    },
    watch: {
      priceByPercentage(val) {
        if (!isNaN(val)) {
          for (let i = 0; i < this.distinctPrices.length; i++) {
            this.newPrices[this.distinctPrices[i]] = parseFloat(this.distinctPrices[i]) + parseFloat(this.distinctPrices[i]) * val / 100
          }
        } else {
          this.newPrices = {}
        }
      },
      'filter.ids': {
        handler (val) {
          if (!this.resetting) {
            if (val.length) {
              this.filters['quoteIds'] = [ 'id', 'in', val ]
            } else {
              this.filters['quoteIds'] = []
            }
          }
        },
        deep: true
      },
      'filter.quotePostalCode': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quotePostalCode'] = [ 'postal_code_to', 'eq', val ]
            } else {
              this.filters['quotePostalCode'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteName': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteName'] = [ 'reference', 'ct', val ]
            } else {
              this.filters['quoteName'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaFrom': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters['quoteAreaFrom'] = [ 'area_id_from', 'eq', val ]
            } else {
              this.filters['quoteAreaFrom'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaTo': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters['quoteAreaTo'] = [ 'area_id_to', 'eq', val ]
            } else {
              this.filters['quoteAreaTo'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteCompanies': {
        handler (val) {
          if (!this.resetting) {
            if (val.length > 0) {
              let companies = val.map(function (item) {
                return item.id
              })
              this.filters['quoteCompanies'] = [ 'company_id_customer', 'in', companies ]
            } else {
              this.filters['quoteCompanies'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteProviders': {
        handler (val) {
          if (!this.resetting) {
            if (val.length > 0) {
              let companies = val.map(function (item) {
                return item.id
              })
              this.filters['quoteProviders'] = [ 'company_id_provider', 'in', companies ]
            } else {
              this.filters['quoteProviders'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteShippingType': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteShippingType'] = [ 'shipping_type_id', 'eq', val ]
            } else {
              this.filters['quoteShippingType'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteShippingSizeType': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteShippingSizeType'] = [ 'size_id', 'eq', val ]
            } else {
              this.filters['quoteShippingSizeType'] = []
            }
          }
        },
        deep: true
      },
      selectAllTrigger(val) {
        if (val === true) {
          this.selectAll()
        } else {
          this.unselectAll()
        }
      },
      selected(val) {
        console.log(val)
      }
    },
    created() {
      this.fetchAreas()
      this.$http.get(this.$appConfig.api.baseUrl + 'shipping-types')
        .then(response => {
          this.shippingTypes = response.body.data
          this.shippingTypesSelect = this.shippingTypes.map((item) => {
            return {
              value: item.id,
              text: item.name
            }
          })
          this.shippingTypes.unshift({
            id: 'null',
            name: this.$gettext('No specific type')
          })
          this.shippingTypes.unshift({
            id: '',
            name: this.$gettext('All types')
          })
        })
      this.$http.get(this.$appConfig.api.baseUrl + 'sizes')
        .then(response => {
          this.sizeTypes = response.body.data
          this.sizeTypesSelect = this.sizeTypes.map((item) => {
            return {
              value: item.id,
              text: item.name
            }
          })
          this.sizeTypes.unshift({
            id: 'null',
            name: this.$gettext('No specific size')
          })
          this.sizeTypes.unshift({
            id: '',
            name: this.$gettext('All sizes')
          })
          for (let i = 0; i < this.sizeTypesSelect.length; i++) {
            for (let n = 0; n < this.companyPrices.length; n++) {
              if (this.companyPrices[n].size_type_id === this.sizeTypesSelect[i].value) {
                this.sizeTypesSelect.splice(i, 1)
              }
            }
          }
        }).catch(response => {

        })
    },
    methods: {
      resetEditPrices() {
        this.newPrices = {}
        this.priceByPercentage = null
        this.searchFor = null
        this.replaceWith = null
      },
      async bulkReplaceActive() {
        this.updating = true
        this.totalToUpdate = this.selected
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {
          console.log(this.selected[i])

          const payload = {
            active: this.active
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunkLength) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'outsourcing-quotes/' + this.selected[i].id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditPrices.hide()
      },
      async bulkUpdatePrices() {
        this.updating = true
        this.totalToUpdate = this.selected.filter(x => Object.keys(this.newPrices).indexOf(x.price) !== -1)
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {
          let payload = {}
          if (this.newPrices[this.selected[i].price]) {
            payload.price = this.newPrices[this.selected[i].price]
            if (this.pricesAsIntegers && !isNaN(payload.price)) {
              payload.price = parseInt(payload.price)
            }
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunkLength) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'outsourcing-quotes/' + this.selected[i].id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditPrices.hide()
      },
      handleFileImportedSelect(evt) {
        let file = this.fileImported
        if (file) {
          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            complete: (results) => {
              this.filter.ids = results.data.map(id => { return id.quote_id })
            }
          })
        }
      },
      handleFileSelect(evt) {
        let file = this.file
        if (file) {
          const _this = this

          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            complete: function (results) {
              _this.errors = []
              _this.importData = []
              if (results.errors.length === 0) {
                if (
                  results.meta.fields.indexOf('area_id_from') !== -1 &&
                  results.meta.fields.indexOf('area_id_to') !== -1 &&
                  results.meta.fields.indexOf('company_id_provider') !== -1 &&
                  results.meta.fields.indexOf('price') !== -1
                ) {
                  _this.importData = results.data
                  // console.log((_this.importData[1].companies + '').split(','))
                } else {
                  _this.errors = [
                    {
                      message: this.$gettext('There are one or more missing required data columns')
                    }
                  ]
                }
              } else {
                _this.errors = results.errors
                console.log(results.errors)
              }
            }
          })
        }
      },
      async bulkImport() {
        this.importing = true
        this.count = 0
        let count = 0
        let ids = []
        this.errors = []
        let createdAtFrom
        let createdAtTo
        for (let i = 0; i < this.importData.length; i++) {
          let quote = JSON.parse(JSON.stringify(this.importData[i]).replace(/{([a-zA-Z]+):[^}]+}/g, '{$1}'))

          let paramsQuotes = {
            area_id_from: quote.area_id_from,
            area_id_to: quote.area_id_to,
            postal_code_to: quote.postal_code_to,
            company_id_customer: quote.company_id_customer,
            company_id_provider: quote.company_id_provider,
            price: quote.price,
            size_id: quote.date_from,
            shipping_type_id: quote.shipping_type_id,
            applies_automatically: quote.applies_automatically,
            distribution_factor: quote.distribution_factor,
            active: quote.active
          }
          await this.$http.post(this.$appConfig.api.baseUrl + 'outsourcing-quotes', paramsQuotes)
            .then(response => {
              createdAtFrom = !createdAtFrom ? response.body.created_at : createdAtFrom

              ids.push({quote_id: response.body.id})
              count++
              this.count = count
              createdAtTo = response.body.created_at
            })
            .catch(e => {
              this.$root.$children[0].$refs.simplert.justCloseSimplert()
              count++
              this.count = count
              this.errors.push({...e.body, row: i + 2})
              this.errors = this.errors.sort((a, b) => a.row - b.row)
            })
        }
        let filename = 'imported_quote_3p__' + createdAtTo + '.csv'
        this.exportToCsv(filename, Papa.unparse(ids))
        this.file = null
        this.importData = []
        this.importing = false
        this.filters.createdAtFrom = ['created_at', 'gte', createdAtFrom]
        this.filters.createdAtTo = ['created_at', 'lte', this.moment.utc(createdAtTo).add(5, 'seconds').format('YYYY-MM-DD HH:mm:ss')]
        this.searchQuotes()
      },
      async searchQuotes() {
        this.selectAllTrigger = false
        this.total = null
        this.working = true
        let params = {
          page: 0,
          limit: 500,
          sort: [
            {
              key: 'id',
              direction: 'desc'
            }
          ],
          includes: [
            'company',
            'provider',
            'area_from',
            'area_to',
            'size',
            'shipping_type'
          ],
          filter_groups: []
        }
        if (!this.$cookies.get('bypassCountry')) {
          params.filter_groups.push(
            {
              filters: [
                [this.$appConfig.models['outsourcing-quotes'].countrize, 'eq', window.country]
              ]
            }
          )
        }
        if (this.filters) {
          params.filter_groups.push(
            {filters: Object.values(this.filters)}
          )
        }
        await this.doSearchQuotes(params)
        this.working = false
      },
      async doSearchQuotes(params, prevQuotes = []) {
        let searchedQuotes = []
        return await this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['outsourcing-quotes'].endpoint, {params})
          .then(async response => {
            searchedQuotes = response.body.data

            searchedQuotes = prevQuotes.concat(searchedQuotes)
            if (response.body.data.length !== 0) {
              params.page = params.page + 1
              await this.doSearchQuotes(params, searchedQuotes)
            } else {
              this.total = searchedQuotes.length
              this.searchedQuotes = searchedQuotes
            }
          })
      },
      resetQuoteSearch() {
        this.total = null
        this.resetting = true
        this.searchedQuotes = []
        this.filters = []
        this.fileImported = null
        Object.assign(this.$data.filter, this.$options.data.call(this).filter)
        setTimeout(function () {
          this.resetting = false
        }.bind(this), 500)
      },
      rowSelected(items) {
        this.selected = items
      },
      selectAll() {
        let quoteProvidersTable = this.$refs.quoteProvidersTable.$el
        let tableBody = quoteProvidersTable.getElementsByTagName('tbody')[0]
        let tableRows = tableBody.getElementsByTagName('tr')
        this.unselectAll()
        for (const row of tableRows) {
          row.click()
        }
      },
      unselectAll() {
        this.$refs.quoteProvidersTable.clearSelected()
      },
      onSearchCompanySelected(item) {
        this.filter.quoteCompanies.push({id: item.id, name: item.name})
        this.$refs.companyAc.searchQuery = ''
      },
      removeCompanyFromFilters(index) {
        this.filter.quoteCompanies.splice(index, 1)
      },
      onSearchProviderSelected(item) {
        this.filter.quoteProviders.push({id: item.id, name: item.name})
        this.$refs.providerAc.searchQuery = ''
      },
      removeProviderFromFilters(index) {
        this.filter.quoteProviders.splice(index, 1)
      },
      backupSelected() {
        let quotesExport = []
        for (let i = 0; i < this.selected.length; i++) {
          let originalQuote = JSON.parse(JSON.stringify(this.selected[i]))

          try {
            let theQuote = originalQuote
            delete (theQuote.id)
            delete (theQuote.created_at)
            delete (theQuote.updated_at)
            delete (theQuote.deleted_at)
            delete (theQuote.company)
            delete (theQuote.provider)
            delete (theQuote.area_from)
            delete (theQuote.area_to)
            delete (theQuote.shipping_type)
            delete (theQuote.size)
            quotesExport.push(theQuote)
          } catch (e) {
            console.log(e)
          }
        }

        let filename = 'quote_3p_backup'
        Object.values(this.filters).forEach(filter => {
          if (filter.length > 0) {
            filename += '__' + filter.join('-')
          }
        })
        filename += '.csv'
        this.exportToCsv(filename, Papa.unparse(quotesExport))
        this.backuped = true
      },
      async deleteSelected() {
        if (
          confirm(this.$gettext('Are you sure you want to DELETE the selected rows?\nWARNING: THIS ACTION CANNOT BE UNDONE!'))
        ) {
          this.deleting = true
          this.count = 0
          let count = 0
          for (let i = 0; i < this.selected.length; i++) {
            let theQuote = this.selected[i]
            this.$http.delete(this.$appConfig.api.baseUrl + 'outsourcing-quotes/' + theQuote.id)
              .finally(response => {
                count++
                this.count = count
                if (count === this.selected.length) {
                  this.deleting = false
                  this.$router.push({ name: 'modelList', params: { type: 'outsourcing-quotes' }, query: { deleted: true } })
                }
              })
          }
        }
      },
      exportToCsv(filename, csv) {
        let blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        if (navigator.msSaveBlob) { // IE 10+
          navigator.msSaveBlob(blob, filename)
        } else {
          let link = document.createElement('a')
          if (link.download !== undefined) { // feature detection
            // Browsers that support HTML5 download attribute
            let url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', filename)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        }
      }
    }
  }
</script>
